'use client';

import { useUser } from '@auth0/nextjs-auth0/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { redirectURLOrProjects } from '@/app/helpers/auth';

import {
  buildPopupLoginUrl,
  buildPopupWindowFeatures,
  isEmbeddedInIframe,
  shouldShowIframeLoginPrompt,
} from './iframeLoginFlowUtils';

export function useIframeLoginFlow() {
  const [isInIframe, setIsInIframe] = useState<boolean | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [loginRedirect, setLoginRedirect] = useState<string | null>(null);
  const popupCloseCheckInterval = useRef<number | null>(null);

  const { user, isLoading } = useUser();
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    setIsInIframe(isEmbeddedInIframe(window));

    const redirect = redirectURLOrProjects(
      searchParams.get('loginRedirect'),
      window.location.hostname,
    );
    setLoginRedirect(redirect);
  }, [searchParams]);

  useEffect(() => {
    if (isLoading || isInIframe === null || loginRedirect === null) {
      return;
    }

    if (user) {
      router.push(loginRedirect);
      return;
    }

    if (!isInIframe) {
      const connection = searchParams.get('connection');
      const returnTo = encodeURIComponent(loginRedirect);
      const connectionParam = connection
        ? `&connection=${encodeURIComponent(connection)}`
        : '';
      window.location.href = `/api/auth/login?returnTo=${returnTo}${connectionParam}`;
    }
  }, [isLoading, isInIframe, loginRedirect, router, user, searchParams]);

  // Listen for session token from popup and exchange it for a partitioned cookie.
  // This bypasses third-party cookie blocking in cross-origin iframes.
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'SESSION_TOKEN' && event.data.token) {
        try {
          // eslint-disable-next-line custom-rules/enforce-use-query-for-api-calls
          const res = await fetch('/api/auth/exchange-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: event.data.token }),
          });

          if (res.ok) {
            window.location.reload();
          }
        } catch {
          // Exchange failed - popup.closed check will reload as fallback
        }
      }

      if (event.data?.type === 'AUTH_SUCCESS') {
        window.location.reload();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(
    () => () => {
      if (popupCloseCheckInterval.current !== null) {
        window.clearInterval(popupCloseCheckInterval.current);
      }
    },
    [],
  );

  const handlePopupLogin = useCallback(() => {
    const loginUrl = buildPopupLoginUrl(window.location.origin);
    const popup = window.open(
      loginUrl,
      'auth0-login',
      buildPopupWindowFeatures(window),
    );

    if (!popup) {
      setPopupBlocked(true);
      return;
    }

    setPopupBlocked(false);

    if (popupCloseCheckInterval.current !== null) {
      window.clearInterval(popupCloseCheckInterval.current);
    }

    popupCloseCheckInterval.current = window.setInterval(() => {
      if (popup.closed) {
        if (popupCloseCheckInterval.current !== null) {
          window.clearInterval(popupCloseCheckInterval.current);
          popupCloseCheckInterval.current = null;
        }
        window.location.reload();
      }
    }, 500);
  }, []);

  return {
    handlePopupLogin,
    popupBlocked,
    showIframeLoginPrompt: shouldShowIframeLoginPrompt({
      isInIframe,
      isLoading,
      isAuthenticated: Boolean(user),
    }),
  };
}
